import React, { createContext, useContext, useMemo } from 'react';

import { useGetMeQuery, GetMeQuery } from 'graphql/types';
import Loading from 'modules/loading/Loading';
import { useTriggerMlTraining } from 'modules/App/hooks/useTriggerMlTraining';
import { useBasicTimeSignalsCount } from 'modules/notes/hooks/useBasicTimeSignalsCount';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import { routePathMap } from 'modules/common';
import Theme from '../../Theme';
import { useSentryUserContext } from './useMeSentryContext';

export type MeQueryType = {
  landingPath?: string;
} & GetMeQuery['me'];

export const MeContext = createContext<MeQueryType | null>(null);

export const useMeContext = (): MeQueryType | null => useContext(MeContext);

const MeContextProvider: React.FunctionComponent = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const { loading, data } = useGetMeQuery({ fetchPolicy: 'cache-first' });
  const startDate = MIN_SUPPORTED_DATE;

  const {
    loading: countLoading,
    data: countData,
    error: countError
  } = useBasicTimeSignalsCount(startDate);
  const meData = useMemo(() => {
    if (data?.me && (countData || countError)) {
      if (
        data.me.isAutoAcceptEnabled &&
        countData?.unClassified.totalCount === 0 &&
        countData?.accepted.totalCount > 0
      ) {
        return {
          ...data?.me,
          landingPath: routePathMap.smartDiary
        };
      } else {
        return data?.me;
      }
    }

    return data?.me;
  }, [countData, countError, data?.me]) as MeQueryType;

  useSentryUserContext(data);
  useTriggerMlTraining();

  if (loading || countLoading) {
    return (
      <Theme direction={'ltr'}>
        <Loading />
      </Theme>
    );
  }

  return <MeContext.Provider value={meData}>{children}</MeContext.Provider>;
};

export default MeContextProvider;
